import React, { useEffect } from 'react'
import { useThrottledCallback } from 'use-debounce'

import { View } from 'react-native'
import { scheduleOnRN } from 'react-native-worklets'
import Animated, {
  clamp,
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDecay,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { StyleSheet } from 'react-native'
import { useQrScanner } from './useQrScanner'
import { BarcodeScanningResult } from 'expo-camera'

import { useToast } from '@ui/toasts/useToastManager'
import { useTranslation } from 'react-i18next'
import { resolveDevToggle, useDevToggles } from 'hooks/dev/useDevToggles'
import { useMaxWindowDimensions } from '@ui/theme/useRWD'
import { useAppActive } from 'hooks/activity/useAppActivity'
import CodeScanner from './CodeScanner'

const MaxOffset = 450
const CloseThreshold = 150

const QrModalContent = ({
  visible,
  close,
}: {
  visible: boolean
  close: () => void
}) => {
  const { width: windowW, height: windowH } = useMaxWindowDimensions()
  const SIZE = 450

  const translationY = useSharedValue(0)
  const prevTranslationY = useSharedValue(0)
  const { t } = useTranslation('ui')
  const handleResult = useQrScanner((s) => s.handleResult)

  useEffect(() => {
    if (visible) {
      translationY.value = MaxOffset
      translationY.set(
        withTiming(0, {
          duration: 300,
          easing: Easing.inOut(Easing.ease),
        })
      )
    }
  }, [visible])

  const animatedStyles = useAnimatedStyle(() => ({
    transform: [
      { translateY: translationY.value },
      {
        scale: interpolate(
          Math.abs(translationY.value),
          [0, CloseThreshold, MaxOffset],
          [1, 1, 0.5]
        ),
      },
    ],
    opacity: interpolate(
      Math.abs(translationY.value),
      [0, CloseThreshold, MaxOffset],
      [1, 1, 0]
    ),
    top: Math.round(windowH / 2 - SIZE / 2),
    left: Math.round(windowW / 2 - SIZE / 2),
    width: 450,
    height: 450,
  }))

  const pan = Gesture.Pan()
    .minDistance(1)
    .onStart(() => {
      prevTranslationY.value = translationY.value
    })
    .onUpdate((event) => {
      translationY.value = clamp(
        prevTranslationY.value + event.translationY,
        -MaxOffset,
        MaxOffset
      )
    })
    .onFinalize((event) => {
      const diff = Math.abs(translationY.value - prevTranslationY.value)
      if (diff < CloseThreshold) {
        return
      }
      translationY.set(
        withDecay({
          velocity: event.velocityY,
          clamp: [-MaxOffset, MaxOffset],
        })
      )
    })
    .onEnd(() => {
      const diff = Math.abs(translationY.value - prevTranslationY.value)
      if (diff < CloseThreshold) {
        translationY.set(withSpring(0))
      } else {
        scheduleOnRN(close)
      }
    })

  const toast = useToast()

  const handleScan = useThrottledCallback(
    (res: BarcodeScanningResult) => {
      toast({
        variant: 'info',
        title: t('ui:qrCodeScanned'),
      })
      handleResult(res?.data)
    },
    3000,
    {
      trailing: false,
      leading: true,
    }
  )

  const cameraType = useDevToggles((s) => s.qrCameraType)

  useEffect(() => {
    // Cancel throttled invocations on unmount
    return () => handleScan.cancel()
  }, [])

  // We can unmount the scanner when the app is inactive
  const isActive = useAppActive()
  const scannerMounted = isActive || visible

  return (
    <Animated.View
      style={[animatedStyles]}
      className="absolute  w-full flex-1 overflow-hidden rounded-2xl"
    >
      {scannerMounted && (
        <CodeScanner
          // Remount the scanner when the visibility changes to avoid freezes
          key={`${visible}`}
          facing={resolveDevToggle(cameraType, 'front')}
          className="flex-1"
          onBarcodeScanned={handleScan}
        />
      )}
      <GestureDetector gesture={pan}>
        <View style={StyleSheet.absoluteFillObject} />
      </GestureDetector>
    </Animated.View>
  )
}

export default QrModalContent
